import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { pointInGeoJSON } from '../../utils/geospatial';
import { db } from '../../firebase-config';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { setAdafruitLocations } from './locationsSlice';

// When using Firestore emulator, use polling instead of onSnapshot to avoid SDK "Unexpected state" assertion
const useFirestoreEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true' || process.env.REACT_APP_ENVIRONMENT === 'emulator';
const EMULATOR_POLL_INTERVAL_MS = 5000;

/** Recursively serialize Firestore types (GeoPoint, etc.) so Redux payloads are serializable. */
function serializeForRedux(value) {
  if (value === null || value === undefined) return value;
  const lat = value.latitude ?? value._lat;
  const lng = value.longitude ?? value._long;
  if (typeof lat === 'number' && typeof lng === 'number') {
    return { latitude: lat, longitude: lng };
  }
  if (value && typeof value.toDate === 'function') {
    return value.toDate().toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(serializeForRedux);
  }
  if (typeof value === 'object' && value.constructor === Object) {
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = serializeForRedux(value[key]);
    }
    return out;
  }
  return value;
}

// Store the Firebase unsubscribe function outside of Redux
let realtimeUnsubscribe = null;

// Export a cleanup function for the realtime listener
export const cleanupRealtimeListener = () => {
  if (realtimeUnsubscribe) {
    realtimeUnsubscribe();
    realtimeUnsubscribe = null;
  }
};

// Async thunk for fetching all devices based on user permissions
export const fetchAllDevices = createAsyncThunk(
  'devices/fetchAllDevices',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) {
        throw new Error("User organization not found.");
      }

      console.log("🔍 Permission check debug:", {
        hasPermissions: !!permissions,
        viewVehicles: permissions?.viewVehicles,
        isArray: Array.isArray(permissions?.viewVehicles),
        length: permissions?.viewVehicles?.length,
        isEmpty: Array.isArray(permissions?.viewVehicles) && permissions.viewVehicles.length === 0
      });

      // Check if user has vehicle viewing permissions
      const hasVehiclePermission = permissions?.viewVehicles === true ||
        (Array.isArray(permissions?.viewVehicles) && permissions.viewVehicles.length > 0);

      if (!hasVehiclePermission) {
        console.log("🚫 User does not have permission to view any vehicles.");
        return []; // Return empty array if no permission
      }

      // New structure: vehicles are in subcollections under vehicle types
      console.log(`🔍 Fetching vehicles from subcollections for organization: ${orgId}`);

      // Get all vehicle type documents first
      const vehiclesRef = collection(db, 'vehicles');
      const vehicleTypesSnapshot = await getDocs(vehiclesRef);

      let allDevices = [];

      // Process each vehicle type's subcollection
      for (const vehicleTypeDoc of vehicleTypesSnapshot.docs) {
        const vehicleTypeData = vehicleTypeDoc.data();
        const vehicleType = vehicleTypeData.vehicle_type;

        // Skip the "Unknown" type as it doesn't have subcollections
        if (vehicleType === 'Unknown' || !vehicleType) continue;

        console.log(`🔍 Processing vehicle type: ${vehicleType}`);

        // Get vehicles from this type's subcollection
        const vehicleSubcollectionRef = collection(db, 'vehicles', vehicleTypeDoc.id, 'vehicles');
        let subQuery;

        if (permissions?.viewVehicles === true) {
          // Admin case: fetch all vehicles for the organization
          subQuery = query(vehicleSubcollectionRef, where("organizationId", "==", orgId));
        } else if (Array.isArray(permissions?.viewVehicles) && permissions.viewVehicles.length > 0) {
          // NEW COLLECTION-BASED PERMISSIONS: Check if user has access to this collection
          const hasCollectionAccess = permissions.viewVehicles.includes(vehicleType.toLowerCase());

          if (hasCollectionAccess) {
            // User has access to this collection - fetch all vehicles from it
            subQuery = query(vehicleSubcollectionRef, where("organizationId", "==", orgId));
          } else {
            // User doesn't have access to this collection - skip it
            continue;
          }
        } else {
          continue; // Skip this vehicle type if no permission
        }

        const subQuerySnapshot = await getDocs(subQuery);
        console.log(`📊 Vehicle type ${vehicleType} returned ${subQuerySnapshot.docs.length} documents`);

        const typeDevices = subQuerySnapshot.docs.map(doc => {
          const data = doc.data();
          // Convert GeoPoint to a serializable object for Redux and Leaflet
          if (data.location) {
            data.location = {
              latitude: data.location.latitude,
              longitude: data.location.longitude,
            };
          }
          // Add vehicle type from parent collection and preserve Vehicletype from raw_data
          const device = {
            id: doc.id,
            ...data,
            vehicleType: vehicleType,
            parentVehicleType: vehicleTypeDoc.id,
            // Preserve Vehicletype from raw_data for filtering
            Vehicletype: data.Vehicletype || data.raw_data?.Vehicletype || ''
          };

          // Debug: Log device structure for Cars and Jeeps
          if (data.raw_data?.Vehicletype === 'Car' || data.raw_data?.Vehicletype === 'Jeep') {
            console.log('🚗 Found Car/Jeep device:', {
              id: doc.id,
              name: device.name,
              vehicleType: vehicleType,
              rawDataVehicletype: data.raw_data?.Vehicletype,
              device: device
            });
          }

          return device;
        });

        allDevices = allDevices.concat(typeDevices);
      }

      console.log(`📊 Total vehicles fetched: ${allDevices.length}`);
      const devices = allDevices;

      // Format devices with proper device type mapping
      const formattedLocations = devices.map(loc => {
        const originalName = loc.displayName || loc.name || loc.raw_data?.name || loc.raw_data?.DeviceName || loc.id;
        let displayName = originalName;

        // Clean display name by removing collection prefixes
        const collectionPrefixes = ['office vehicle', 'operation vehicle', 'store vehicles', 'office_vehicle', 'operation_vehicle', 'store_vehicles'];
        for (const prefix of collectionPrefixes) {
          if (displayName.toLowerCase().startsWith(prefix.toLowerCase())) {
            displayName = displayName.substring(prefix.length).trim();
            break;
          }
        }

        // Determine device type based on vehicle type and name patterns
        let deviceType = 'vehicle'; // default

        // Check vehicle type from parent collection
        const vehicleType = loc.vehicleType || loc.deviceType || 'unknown';

        // Check Vehicletype field first (highest priority) - applies to all devices
        const actualVehicleType = loc.Vehicletype || loc.raw_data?.Vehicletype || '';
        if (actualVehicleType.toLowerCase() === 'jeep' || actualVehicleType.toLowerCase() === 'car') {
          deviceType = 'jeep';
        } else if (actualVehicleType.toLowerCase() === 'atv') {
          deviceType = 'atv';
        } else if (actualVehicleType.toLowerCase() === 'boat' || actualVehicleType.toLowerCase() === 'sailboat' || actualVehicleType.toLowerCase() === 'sailingship') {
          deviceType = 'boat';
        } else if (actualVehicleType.toLowerCase() === 'truck' || actualVehicleType.toLowerCase() === 'mini tempo' || actualVehicleType.toLowerCase() === 'hiab truck') {
          deviceType = 'truck';
        } else if (actualVehicleType.toLowerCase() === 'bike' || actualVehicleType.toLowerCase() === 'motorcycle' || actualVehicleType.toLowerCase() === 'scooter') {
          deviceType = 'bike';
        } else {
          // Fallback to collection-based mapping
          switch (vehicleType.toLowerCase()) {
            case 'atv':
              deviceType = 'atv';
              break;
            case 'ebike':
              deviceType = 'ebike';
              if (originalName.includes('_')) {
                displayName = originalName.split('_')[0];
              }
              break;
            case 'office vehicle':
            case 'operation vehicle':
            case 'store vehicles':
              deviceType = 'jeep'; // Default for these collections when no Vehicletype
              break;
            case 'triton vts':
              deviceType = 'triton';
              break;
            default:
              // Fallback to name-based detection
              const name = originalName.toLowerCase();
              if (name.includes('drishti') || name.includes('m.v') || name.includes('m.l')) {
                deviceType = 'boat';
              } else if (name.includes('beach_vehicle')) {
                deviceType = 'jeep';
              } else if (name.includes('atv')) {
                deviceType = 'atv';
              } else if (name.includes('ebike') || name.includes('eb')) {
                deviceType = 'ebike';
              } else if (name.includes('triton') || name.includes('ns')) {
                deviceType = 'triton';
              }
              break;
          }
        }

        return {
          device_id: loc.id,
          unique_key: `${loc.id}_${loc.parentVehicleType}`, // Create unique key for React
          name: originalName,
          displayName: displayName,
          last_latitude: loc.location?.latitude || loc.latitude || loc.raw_data?.location?.latitude || loc.raw_data?.latitude || null,
          last_longitude: loc.location?.longitude || loc.longitude || loc.raw_data?.location?.longitude || loc.raw_data?.longitude || null,
          battery_level: loc.battery,
          status: loc.status || 'unknown',
          last_seen: loc.lastSeen?.toDate?.().toISOString() || new Date().toISOString(),
          device_type: deviceType,
          vehicle_type: vehicleType,
          vehicleType: vehicleType, // Add camelCase version for frontend compatibility
          assetType: loc.assetType || deviceType,
          parentVehicleType: loc.parentVehicleType, // Keep parent info for debugging
          // Preserve Vehicletype for filtering
          Vehicletype: loc.Vehicletype || '',
          raw_data: loc.raw_data || {}
        };
      });

      // No additional correction needed as device types are properly mapped above
      const correctedDevices = formattedLocations;

      // Check for duplicate device_ids and log them
      const deviceIdCounts = {};
      correctedDevices.forEach(device => {
        deviceIdCounts[device.device_id] = (deviceIdCounts[device.device_id] || 0) + 1;
      });

      const duplicates = Object.entries(deviceIdCounts).filter(([id, count]) => count > 1);
      if (duplicates.length > 0) {
        console.warn(`⚠️ Found ${duplicates.length} duplicate device IDs:`, duplicates.map(([id, count]) => `${id} (${count}x)`));
        console.warn(`🔍 Using unique_key to resolve React key conflicts`);
      }

      console.log(`✅ Returning ${correctedDevices.length} devices to Redux store`);
      return correctedDevices;

    } catch (error) {
      console.error("Error fetching from Firestore:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching individual device details
export const fetchDeviceDetails = createAsyncThunk(
  'devices/fetchDeviceDetails',
  async (deviceId, { rejectWithValue }) => {
    try {
      const docRef = doc(db, 'vehicles', deviceId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error(`Device with ID ${deviceId} not found.`);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Real-time listener setup for device updates
export const setupRealtimeDeviceListener = createAsyncThunk(
  'devices/setupRealtimeDeviceListener',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId || !permissions) {
        throw new Error("User organization or permissions not found.");
      }

      const hasVehiclePermission = permissions?.viewVehicles === true || (Array.isArray(permissions?.viewVehicles) && permissions.viewVehicles.length > 0);
      if (!hasVehiclePermission) {
        console.log("🚫 User does not have permission to view any vehicles for real-time updates.");
        return null;
      }

      // Clean up any existing listeners before setting up new ones
      if (realtimeUnsubscribe) {
        realtimeUnsubscribe();
        realtimeUnsubscribe = null;
      }

      const vehicleTypesSnapshot = await getDocs(collection(db, 'vehicles'));
      const unsubscribes = [];

      const processSnapshot = (querySnapshot, vehicleType, parentVehicleType) => {
        const devices = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            vehicleType: vehicleType,
            parentVehicleType: parentVehicleType,
            Vehicletype: data.Vehicletype || data.raw_data?.Vehicletype || ''
          };
        });

        // Instead of dispatching here, we should aggregate and then dispatch
        return devices;
      };

      let allDevicesFromListeners = [];
      const vehicleTypeDocs = vehicleTypesSnapshot.docs;

      if (useFirestoreEmulator) {
        // Emulator: single polling loop for all vehicle types to avoid onSnapshot "Unexpected state"
        const queriesToPoll = [];
        for (const vehicleTypeDoc of vehicleTypeDocs) {
          const vehicleTypeData = vehicleTypeDoc.data();
          const vehicleType = vehicleTypeData.vehicle_type;
          if (vehicleType === 'Unknown' || !vehicleType) continue;
          const hasCollectionAccess = permissions.viewVehicles === true || (Array.isArray(permissions.viewVehicles) && permissions.viewVehicles.includes(vehicleType.toLowerCase()));
          if (hasCollectionAccess) {
            const vehicleSubcollectionRef = collection(db, 'vehicles', vehicleTypeDoc.id, 'vehicles');
            const q = query(vehicleSubcollectionRef, where("organizationId", "==", orgId));
            queriesToPoll.push({ q, vehicleType, parentId: vehicleTypeDoc.id });
          }
        }
        const poll = async () => {
          try {
            let aggregated = [];
            for (const { q, vehicleType, parentId } of queriesToPoll) {
              const snapshot = await getDocs(q);
              aggregated = [...aggregated, ...processSnapshot(snapshot, vehicleType, parentId)];
            }
            const formattedAndCorrected = formatAndCorrectDevices(aggregated);
            dispatch(updateDevicesRealtime(formattedAndCorrected));
          } catch (err) {
            console.error('Emulator devices poll error:', err);
            dispatch(setRealtimeError(err.message));
          }
        };
        poll();
        const intervalId = setInterval(poll, EMULATOR_POLL_INTERVAL_MS);
        unsubscribes.push(() => clearInterval(intervalId));
      } else {
        for (const vehicleTypeDoc of vehicleTypeDocs) {
          const vehicleTypeData = vehicleTypeDoc.data();
          const vehicleType = vehicleTypeData.vehicle_type;

          if (vehicleType === 'Unknown' || !vehicleType) continue;

          const hasCollectionAccess = permissions.viewVehicles === true || (Array.isArray(permissions.viewVehicles) && permissions.viewVehicles.includes(vehicleType.toLowerCase()));

          if (hasCollectionAccess) {
            const vehicleSubcollectionRef = collection(db, 'vehicles', vehicleTypeDoc.id, 'vehicles');
            const q = query(vehicleSubcollectionRef, where("organizationId", "==", orgId));

            const unsubscribe = onSnapshot(q, (snapshot) => {
              const newDevices = processSnapshot(snapshot, vehicleType, vehicleTypeDoc.id);

              // Update the aggregate list
              allDevicesFromListeners = [
                ...allDevicesFromListeners.filter(d => d.parentVehicleType !== vehicleTypeDoc.id),
                ...newDevices
              ];

              const formattedAndCorrected = formatAndCorrectDevices(allDevicesFromListeners);
              dispatch(updateDevicesRealtime(formattedAndCorrected));

            }, (error) => {
              console.error(`Real-time listener error for ${vehicleType}:`, error);
              // On internal Firestore assertion errors (ca9), tear down all listeners
              // and fall back to a one-time fetch to avoid a broken watch stream state.
              if (error.message?.includes('INTERNAL ASSERTION FAILED')) {
                console.warn('Firestore internal error detected — tearing down all listeners and falling back to one-time fetch.');
                if (realtimeUnsubscribe) {
                  realtimeUnsubscribe();
                  realtimeUnsubscribe = null;
                }
                getDocs(q).then(snapshot => {
                  const newDevices = processSnapshot(snapshot, vehicleType, vehicleTypeDoc.id);
                  allDevicesFromListeners = [
                    ...allDevicesFromListeners.filter(d => d.parentVehicleType !== vehicleTypeDoc.id),
                    ...newDevices
                  ];
                  dispatch(updateDevicesRealtime(formatAndCorrectDevices(allDevicesFromListeners)));
                }).catch(console.error);
              } else {
                dispatch(setRealtimeError(error.message));
              }
            });
            unsubscribes.push(unsubscribe);
          }
        }
      }

      // Combine all unsubscribe functions into one
      realtimeUnsubscribe = () => {
        unsubscribes.forEach(unsub => unsub());
      };

      return { success: true, listenerActive: true };

    } catch (error) {
      console.error("Error setting up real-time listeners:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Helper function to format and correct device data
const formatAndCorrectDevices = (devices) => {
  const formattedLocations = devices.map(loc => {
    const originalName = loc.displayName || loc.name || loc.raw_data?.name || loc.raw_data?.DeviceName || loc.id;
    let displayName = originalName;

    const collectionPrefixes = ['office vehicle', 'operation vehicle', 'store vehicles', 'office_vehicle', 'operation_vehicle', 'store_vehicles'];
    for (const prefix of collectionPrefixes) {
      if (displayName.toLowerCase().startsWith(prefix.toLowerCase())) {
        displayName = displayName.substring(prefix.length).trim();
        break;
      }
    }

    let deviceType = 'vehicle';
    const vehicleType = loc.vehicleType || loc.deviceType || 'unknown';
    const actualVehicleType = loc.Vehicletype || loc.raw_data?.Vehicletype || '';

    if (['jeep', 'car'].includes(actualVehicleType.toLowerCase())) deviceType = 'jeep';
    else if (actualVehicleType.toLowerCase() === 'atv') deviceType = 'atv';
    else if (['boat', 'sailboat', 'sailingship'].includes(actualVehicleType.toLowerCase())) deviceType = 'boat';
    else if (['truck', 'mini tempo', 'hiab truck'].includes(actualVehicleType.toLowerCase())) deviceType = 'truck';
    else if (['bike', 'motorcycle', 'scooter'].includes(actualVehicleType.toLowerCase())) deviceType = 'bike';
    else {
      switch (vehicleType.toLowerCase()) {
        case 'atv': deviceType = 'atv'; break;
        case 'ebike':
          deviceType = 'ebike';
          if (originalName.includes('_')) displayName = originalName.split('_')[0];
          break;
        case 'office vehicle':
        case 'operation vehicle':
        case 'store vehicles':
          deviceType = 'jeep';
          break;
        case 'triton vts': deviceType = 'triton'; break;
        default:
          const name = originalName.toLowerCase();
          if (name.includes('drishti') || name.includes('m.v') || name.includes('m.l')) deviceType = 'boat';
          else if (name.includes('beach_vehicle')) deviceType = 'jeep';
          else if (name.includes('atv')) deviceType = 'atv';
          else if (name.includes('ebike') || name.includes('eb')) deviceType = 'ebike';
          else if (name.includes('triton') || name.includes('ns')) deviceType = 'triton';
          break;
      }
    }

    return {
      device_id: loc.id,
      unique_key: `${loc.id}_${loc.parentVehicleType}`,
      name: originalName,
      displayName: displayName,
      last_latitude: loc.location?.latitude || loc.latitude || loc.raw_data?.location?.latitude || loc.raw_data?.latitude || null,
      last_longitude: loc.location?.longitude || loc.longitude || loc.raw_data?.location?.longitude || loc.raw_data?.longitude || null,
      battery_level: loc.battery,
      status: loc.status || 'unknown',
      last_seen: loc.lastSeen?.toDate?.().toISOString() || new Date().toISOString(),
      device_type: deviceType,
      vehicle_type: vehicleType,
      vehicleType: vehicleType,
      assetType: loc.assetType || deviceType,
      parentVehicleType: loc.parentVehicleType,
      Vehicletype: loc.Vehicletype || '',
      raw_data: serializeForRedux(loc.raw_data || {})
    };
  });
  return formattedLocations;
};


const initialState = {
  allDevices: [],
  filteredDevices: [],
  selectedDevice: null,
  dataStatus: 'Loading...',
  lastUpdateTime: null,
  isBackendOnline: false,
  loading: false,
  error: null,
  deviceDetails: {},
  deviceDetailsLoading: false,
  deviceDetailsError: null,
  realtimeError: null,
  isRealtimeActive: false,
};

const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    setSelectedDevice: (state, action) => {
      state.selectedDevice = action.payload;
    },
    clearSelectedDevice: (state) => {
      state.selectedDevice = null;
    },
    updateDataStatus: (state, action) => {
      state.dataStatus = action.payload;
    },
    setBackendOnline: (state, action) => {
      state.isBackendOnline = action.payload;
    },
    updateLastUpdateTime: (state, action) => {
      state.lastUpdateTime = action.payload;
    },
    setFilteredDevices: (state, action) => {
      console.log('🔍 Redux setFilteredDevices called with:', {
        payloadLength: action.payload?.length,
        payloadType: typeof action.payload,
        isArray: Array.isArray(action.payload),
        samplePayload: action.payload?.slice(0, 2)
      });
      state.filteredDevices = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearDeviceDetailsError: (state) => {
      state.deviceDetailsError = null;
    },
    updateDevicesRealtime: (state, action) => {
      console.log(`🔄 Redux reducer: Updating devices with ${action.payload.length} devices`);
      console.log(`🔄 Redux reducer: Sample device:`, action.payload[0]);

      state.allDevices = action.payload;
      state.dataStatus = 'Live';
      state.lastUpdateTime = new Date().toISOString();
      state.isBackendOnline = true;
      state.isRealtimeActive = true;
      state.error = null;
      state.realtimeError = null;
    },
    setRealtimeError: (state, action) => {
      state.realtimeError = action.payload;
      state.isRealtimeActive = false;
    },
    setRealtimeActive: (state, action) => {
      state.isRealtimeActive = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all devices
      .addCase(fetchAllDevices.pending, (state) => {
        state.loading = true;
        state.dataStatus = 'Loading...';
        state.error = null;
      })
      .addCase(fetchAllDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.allDevices = action.payload;
        state.dataStatus = 'Live';
        state.lastUpdateTime = new Date().toISOString();
        state.isBackendOnline = true;
        state.error = null;
      })
      .addCase(fetchAllDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.dataStatus = 'Error';
        state.isBackendOnline = false;
        state.allDevices = [];
      })
      // Fetch device details
      .addCase(fetchDeviceDetails.pending, (state) => {
        state.deviceDetailsLoading = true;
        state.deviceDetailsError = null;
      })
      .addCase(fetchDeviceDetails.fulfilled, (state, action) => {
        state.deviceDetailsLoading = false;
        state.deviceDetails[action.meta.arg] = action.payload;
        state.deviceDetailsError = null;
      })
      .addCase(fetchDeviceDetails.rejected, (state, action) => {
        state.deviceDetailsLoading = false;
        state.deviceDetailsError = action.payload;
      })
      // Setup real-time listener
      .addCase(setupRealtimeDeviceListener.fulfilled, (state, action) => {
        if (action.payload) {
          state.isRealtimeActive = true;
          state.realtimeError = null;
        }
      })
      .addCase(setupRealtimeDeviceListener.rejected, (state, action) => {
        state.realtimeError = action.payload;
        state.isRealtimeActive = false;
      })
      // Keep selected LG Band / Adafruit device in sync with Firestore snapshot (map + panels + follow)
      .addCase(setAdafruitLocations, (state, action) => {
        const sel = state.selectedDevice;
        if (!sel?.device_id) return;
        if (sel.device_type !== 'lg_band' && sel.device_type !== 'adafruit') return;
        const rows = action.payload;
        if (!Array.isArray(rows)) return;
        const row = rows.find((r) => r.device_id === sel.device_id);
        if (!row) return;
        const last_latitude =
          row.coordinates && typeof row.coordinates.lat === 'number'
            ? row.coordinates.lat
            : row.last_latitude;
        const last_longitude =
          row.coordinates && typeof row.coordinates.lng === 'number'
            ? row.coordinates.lng
            : row.last_longitude;
        state.selectedDevice = {
          ...sel,
          ...row,
          device_type: 'lg_band',
          last_latitude,
          last_longitude,
          displayName: row.displayName || row.name || row.deviceId,
        };
      });
  },
});

export const {
  setSelectedDevice,
  clearSelectedDevice,
  updateDataStatus,
  setBackendOnline,
  updateLastUpdateTime,
  setFilteredDevices,
  clearError,
  clearDeviceDetailsError,
  updateDevicesRealtime,
  setRealtimeError,
  setRealtimeActive,
} = devicesSlice.actions;

// Memoized Selector for Filtered Devices

const selectAllDevices = (state) => state.devices.allDevices;
const selectActiveFilters = (state) => state.filters.activeFilters;
const selectTritonLocations = (state) => state.locations.tritonLocations;
const selectAurusLocations = (state) => state.locations.aurusLocations;
const selectAdafruitLocations = (state) => state.locations.adafruitLocations;
const selectSeaHorseLocations = (state) => state.locations.seaHorseLocations;

// Select city/state selection from UI state
const selectSelectedCity = (state) => state.ui.selectedCity;
const selectSelectedState = (state) => state.ui.selectedState;
const selectCityBoundary = (state) => state.ui.cityBoundary;

export const selectFilteredDevices = createSelector(
  [selectAllDevices, selectActiveFilters, selectTritonLocations, selectAurusLocations, selectAdafruitLocations, selectSeaHorseLocations, selectSelectedCity, selectSelectedState, selectCityBoundary],
  (allDevices, activeFilters, tritonLocations, aurusLocations, adafruitLocations, seaHorseLocations, selectedCity, selectedState, cityBoundary) => {
    // Combine all devices into a single array, similar to the original useEffect logic
    const allCombinedDevices = [
      ...allDevices,
      ...tritonLocations.map(triton => ({
        ...triton,
        device_id: triton.id,
        device_type: 'triton',
        last_latitude: triton.coordinates && typeof triton.coordinates.lat === 'number' ? triton.coordinates.lat : triton.last_latitude,
        last_longitude: triton.coordinates && typeof triton.coordinates.lng === 'number' ? triton.coordinates.lng : triton.last_longitude,
        displayName: triton.displayName || triton.name || triton.deviceId
      })),
      ...aurusLocations.map(aurus => ({
        ...aurus,
        device_id: aurus.id,
        device_type: 'aurus',
        last_latitude: aurus.coordinates && typeof aurus.coordinates.lat === 'number' ? aurus.coordinates.lat : aurus.last_latitude,
        last_longitude: aurus.coordinates && typeof aurus.coordinates.lng === 'number' ? aurus.coordinates.lng : aurus.last_longitude,
        displayName: aurus.displayName || aurus.name || aurus.deviceId
      })),
      ...(adafruitLocations || []).map(adafruit => ({
        ...adafruit,
        device_type: 'lg_band',
        last_latitude: adafruit.coordinates && typeof adafruit.coordinates.lat === 'number' ? adafruit.coordinates.lat : adafruit.last_latitude,
        last_longitude: adafruit.coordinates && typeof adafruit.coordinates.lng === 'number' ? adafruit.coordinates.lng : adafruit.last_longitude,
        displayName: adafruit.displayName || adafruit.name || adafruit.deviceId
      })),
      ...(seaHorseLocations || []).map(seahorse => ({
        ...seahorse,
        device_id: seahorse.id,
        device_type: 'seahorse',
        last_latitude: seahorse.coordinates?.lat ?? seahorse.last_latitude,
        last_longitude: seahorse.coordinates?.lng ?? seahorse.last_longitude,
        displayName: seahorse.displayName || seahorse.name
      })),
    ];

    // Deduplicate by device_id — same device can exist in multiple Firestore
    // subcollections (e.g. "ebike" AND "mini_tempo"), causing double rendering.
    // Prefer the entry with an explicit Vehicletype set (more specific data).
    const deviceMap = new Map();
    for (const device of allCombinedDevices) {
      const existing = deviceMap.get(device.device_id);
      if (!existing) {
        deviceMap.set(device.device_id, device);
      } else if (!existing.Vehicletype && device.Vehicletype) {
        // Replace with more specific entry
        deviceMap.set(device.device_id, device);
      }
    }
    const deduplicatedDevices = Array.from(deviceMap.values());

    // First, filter by device type
    let filteredByType = deduplicatedDevices;

    if (!(activeFilters.includes('all') || activeFilters.length === 0)) {
      filteredByType = deduplicatedDevices.filter(device => {
      return activeFilters.some(filter => {
        const vehicleType = device.Vehicletype || device.raw_data?.Vehicletype || device.vehicleType || '';
        const parentVehicleType = device.parentVehicleType || device.vehicleType || '';
        const allowedCollections = ['office_vehicle', 'operation_vehicle', 'store_vehicles'];
        const isFromAllowedCollection = allowedCollections.includes(parentVehicleType.toLowerCase());

        switch (filter) {
          case 'towers':
            return false;
          case 'boat':
            return isFromAllowedCollection && ['sailboat', 'sailingship'].includes(vehicleType.toLowerCase());
          case 'truck':
            return isFromAllowedCollection && ['truck', 'mini tempo', 'hiab truck'].includes(vehicleType.toLowerCase());
          case 'jeep':
            return isFromAllowedCollection && ['jeep', 'car'].includes(vehicleType.toLowerCase());
          case 'bike':
            return isFromAllowedCollection && ['bike', 'motorcycle', 'scooter'].includes(vehicleType.toLowerCase());
          case 'atv':
            return device.device_type === 'atv';
          case 'vehicle':
            const name = (device.name || '').toLowerCase();
            const isSpecialVehicle = name.includes('drishti') || name.includes('m.v') || name.includes('m.l') || name.includes('beach_vehicle') || name.includes('atv');
            return device.device_type === 'vehicle' && !isSpecialVehicle;
          case 'triton':
            return device.device_type === 'triton';
          case 'aurus':
            return device.device_type === 'aurus';
          case 'adafruit':
            return device.device_type === 'adafruit' || device.device_type === 'lg_band';
          case 'lg_band':
            return device.device_type === 'lg_band' || device.device_type === 'adafruit';
          case 'seahorse':
            return device.device_type === 'seahorse';
          case 'mobile':
            return device.device_type === 'mobile';
          case 'ebike':
            return device.device_type === 'ebike';
          case 'beaches':
          case 'swimzone':
            return false; // beaches/swimzones are not devices — handled by separate layers
          default:
            return device.device_type === filter;
        }
      });
    });
    }

    // Then, filter by geographic location (city/state) if selected
    if (cityBoundary && (selectedCity || selectedState)) {
      return filteredByType.filter(device => {
        const isLgBand = device.device_type === 'lg_band' || device.device_type === 'adafruit';
        if (isLgBand) {
          return true;
        }
        // Only filter devices that have valid coordinates
        if (!device.last_latitude || !device.last_longitude ||
            isNaN(device.last_latitude) || isNaN(device.last_longitude)) {
          return false;
        }

        // Check if device coordinates are inside the city/state boundary
        const devicePoint = [device.last_latitude, device.last_longitude];
        return pointInGeoJSON(devicePoint, cityBoundary);
      });
    }

    return filteredByType;
  }
);

export default devicesSlice.reducer;
